import { useEffect, useState } from 'react';
import {
  ChungTuPhaiTraKhac,
  ChungTuPhaiTraKhacInput,
  ChungTuPhaiTraKhacResponse
} from '@/types/schemas/chung-tu-phai-tra-khac.type';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseChungTuPhaiTraKhacReturn {
  chungTuPhaiTraKhacs: ChungTuPhaiTraKhac[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  addChungTuPhaiTraKhac: (newChungTuPhaiTraKhac: ChungTuPhaiTraKhacInput) => Promise<ChungTuPhaiTraKhac>;
  updateChungTuPhaiTraKhac: (
    uuid: string,
    updatedChungTuPhaiTraKhac: ChungTuPhaiTraKhacInput
  ) => Promise<ChungTuPhaiTraKhac>;
  deleteChungTuPhaiTraKhac: (uuid: string) => Promise<void>;
  refreshChungTuPhaiTraKhacs: () => Promise<void>;
  getChungTuPhaiTraKhacByCustomer: (customerId: string) => Promise<ChungTuPhaiTraKhac[]>;
  getChungTuPhaiTraKhacDetail: (uuid: string) => Promise<[]>;
}

/**
 * Hook for managing ChungTuPhaiTraKhac (Other Payable Documents) data
 *
 * This hook provides functions to fetch, create, update, and delete other payable documents and debt offset documents.
 */
export const useChungTuPhaiTraKhac = (
  initialChungTuPhaiTraKhacs: ChungTuPhaiTraKhac[] = []
): UseChungTuPhaiTraKhacReturn => {
  const [chungTuPhaiTraKhacs, setChungTuPhaiTraKhacs] = useState<ChungTuPhaiTraKhac[]>(initialChungTuPhaiTraKhacs);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchChungTuPhaiTraKhacs = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<ChungTuPhaiTraKhacResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHUNG_TU_PHAI_TRA_KHAC}/`
      );
      setChungTuPhaiTraKhacs(response.data.results);
    } catch (error) {
      console.error('Error fetching other payable documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getChungTuPhaiTraKhacByCustomer = async (customerId: string): Promise<ChungTuPhaiTraKhac[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<ChungTuPhaiTraKhacResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHUNG_TU_PHAI_TRA_KHAC}/?ma_kh=${customerId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching other payable documents by customer:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addChungTuPhaiTraKhac = async (newChungTuPhaiTraKhac: ChungTuPhaiTraKhacInput): Promise<ChungTuPhaiTraKhac> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<ChungTuPhaiTraKhac>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHUNG_TU_PHAI_TRA_KHAC}/`,
        newChungTuPhaiTraKhac
      );

      const addedChungTuPhaiTraKhac = response.data;
      setChungTuPhaiTraKhacs(prev => [...prev, addedChungTuPhaiTraKhac]);
      return addedChungTuPhaiTraKhac;
    } catch (error) {
      console.error('Error adding other payable document:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateChungTuPhaiTraKhac = async (
    uuid: string,
    updatedChungTuPhaiTraKhac: ChungTuPhaiTraKhacInput
  ): Promise<ChungTuPhaiTraKhac> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<ChungTuPhaiTraKhac>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHUNG_TU_PHAI_TRA_KHAC}/${uuid}/`,
        updatedChungTuPhaiTraKhac
      );

      const updatedData = response.data;
      setChungTuPhaiTraKhacs(prev => prev.map(item => (item.uuid === uuid ? updatedData : item)));
      return updatedData;
    } catch (error) {
      console.error('Error updating other payable document:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteChungTuPhaiTraKhac = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.CHUNG_TU_PHAI_TRA_KHAC}/${uuid}/`);
      setChungTuPhaiTraKhacs(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting other payable document:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getChungTuPhaiTraKhacDetail = async (uuid: string): Promise<[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<[]>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHUNG_TU_PHAI_TRA_KHAC}/${uuid}/chi-tiet/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching other payable document detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchChungTuPhaiTraKhacs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    chungTuPhaiTraKhacs,
    isLoading,
    isLoadingDetail,
    addChungTuPhaiTraKhac,
    updateChungTuPhaiTraKhac,
    deleteChungTuPhaiTraKhac,
    refreshChungTuPhaiTraKhacs: fetchChungTuPhaiTraKhacs,
    getChungTuPhaiTraKhacByCustomer,
    getChungTuPhaiTraKhacDetail
  };
};
